import React, {useContext, useEffect, useState} from "react";
import {assetsApiClient} from "../AssetsApiClient";
import {useHistory, useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {selectProfile} from "../../ClientManagement/ClientProfile/activeProfileSlice";
import {LegalAgreement} from "../models/InvestmentProgram";
import {RouteWithAssetId} from "../../routes/types";
import DataEntryHeader from "../../components/DataEntry/DataEntryHeader";
import {HoldingAssetSubclassDetails} from "../models/Holding";
import {LoadingIndicator} from "../../pages/LoadingIndicator";
import {Icon} from "../../components";
import {HoldingsScrollableContainer} from '../StandaloneAccount/Holdings/HoldingsScrollableContainer';
import {HoldingSummaryAccordion} from "../StandaloneAccount/Holdings/HoldingSummaryAccordion";
import {IPAccountsHoldingSummarySubclassDetailsRow} from "./IPAccountsHoldingSummarySubclassDetailsRow";
import {useAppInsights} from "../../AppInsights";
import HoldingsInfo from "../StandaloneAccount/Holdings/HoldingsInfo";
import {HoldingSummaryAssetSubclass} from "../StandaloneAccount/Holdings/HoldingSummaryAssetSubclass";
import {AssetClassifications} from "../models/AssetClassifications";
import AssetsViewContext from "../common/AssetsViewContext";
import {DomesticTrustHoldingWriteModel} from "./DomesticTrustAccountWriteModel";
import useProfileEditableState from "../../hooks/useProfileEditableState";
import {HistoryBlockDiscardModal} from "../../components/HistoryBlockDiscardModal/HistoryBlockModal";
import useAnalytics from "../../utils/useAnalytics";
import { useAnalyticsTrackers } from "src/utils/useAnalyticsTrackers";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";
import {
    selectInvestmentProgramLegalAgreementById,
    updateInvestmentProgramLegalAgreementHoldings
} from "../clientAssetsSlice";

export default function LegalAgreementHoldings() {
    const history = useHistory();
    const viewType = useContext(AssetsViewContext);
    const profile = useAppSelector(selectProfile);
    const appInsights = useAppInsights();
    const dispatch = useAppDispatch();
    const [legalAgreement, setLegalAgreement] = useState<LegalAgreement>();
    const [domesticTrustHoldings, setDomesticTrustHoldings] = useState<HoldingAssetSubclassDetails[]>([]);
    const [updatedDomesticTrustHoldings, setUpdatedDomesticTrustHoldings] = useState<HoldingAssetSubclassDetails[]>([]);
    const {assetId: legalAgreementId} = useParams<RouteWithAssetId>();
    const [classifications, setClassifications] = useState<AssetClassifications>();
    const {isProfileWithProposalsOrArchived} = useProfileEditableState();
    const [isDoneButtonDisabled, setIsDoneButtonDisabled] = useState(isProfileWithProposalsOrArchived);
    const [formChanged, setFormChanged] = useState(false)
    const [showNavigationModal, setShowNavigationModal] = useState(true);
    const {page} = useAnalyticsTrackers("enableAssetsGoogleAnalytics");
    const enableOneWDSMigration = useAppSelector(selectReleaseToggles)?.enableOneWDSMigration;
    const legalAgreementFromRedux = useAppSelector(state => selectInvestmentProgramLegalAgreementById(state, legalAgreementId));

    useAnalytics(
        "Navigate_Legal_Agreement_Holding_Form",
        "Navigate_Legal_Agreement_Holding_Form_TimeSpent",
        "enableAssetsGoogleAnalytics"
    );

    useEffect(() => {
        appInsights.startTrackPage("ViewLegalAgreementHoldings")
        const shouldUseReduxSource = !!enableOneWDSMigration && !!legalAgreementFromRedux;

        if (shouldUseReduxSource && legalAgreementFromRedux) {
            assetsApiClient.getAssetClassifications()
                .then((assetClassificationResponse) => {
                    const allHoldings = legalAgreementFromRedux.holdings?.allHoldings ?? [];
                    setDomesticTrustHoldings(allHoldings);
                    setUpdatedDomesticTrustHoldings(allHoldings.map(holding => (
                        {
                            ...holding
                        }))
                    );
                    setLegalAgreement(legalAgreementFromRedux);
                    setClassifications(assetClassificationResponse);
                })
                .catch(reason => console.log(reason))
                .finally(() => {
                    appInsights.stopTrackPage("ViewLegalAgreementHoldings", window.location.href, {
                        profileId: profile.id,
                        legalAgreementId: legalAgreementId
                    });
                });
            return;
        }

        // TODO enableOneWDSMigration cleanup: Remove this fallback once this feature is in Prod
        Promise.all([
            assetsApiClient.getDomesticTrustHoldings(profile.id, legalAgreementId, "IP"),
            assetsApiClient.getLegalAgreement(profile.id, legalAgreementId),
            assetsApiClient.getAssetClassifications()
        ])
            .then(([holdingsResponse, legalAgreementResponse, assetClassificationResponse]) => {
                setDomesticTrustHoldings(holdingsResponse.allHoldings);
                setUpdatedDomesticTrustHoldings(holdingsResponse.allHoldings.map(holding => (
                    {
                        ...holding
                    }))
                );
                setLegalAgreement(legalAgreementResponse);
                setClassifications(assetClassificationResponse);
            })
            .catch(reason => console.log(reason))
            .finally(() => {
                appInsights.stopTrackPage("ViewLegalAgreementHoldings", window.location.href, {
                    profileId: profile.id,
                    legalAgreementId: legalAgreementId
                });
            });
    }, [profile.id, legalAgreementId, enableOneWDSMigration, legalAgreementFromRedux]);

    const handleLockHolding = (index: number, checked: boolean) => {
        handleAssetSubclassDetailUpdate(index, (assetSubclassDetail: HoldingAssetSubclassDetails) => {
            assetSubclassDetail.locked = checked
        })
        setFormChanged(true)
    }

    const handleConcentrateHolding = (index: number, checked: boolean) => {
        handleAssetSubclassDetailUpdate(index, (assetSubclassDetail: HoldingAssetSubclassDetails) => {
            assetSubclassDetail.concentrated = checked
        })
        setFormChanged(true)
    }

    const handleAssetSubclassDetailUpdate = (index: number, changeFunction: (assetSubclassDetail: HoldingAssetSubclassDetails) => void) => {
        if (index >= 0) {
            setUpdatedDomesticTrustHoldings(currentHoldings => currentHoldings.map((holding, currentIndex) => {
                if (currentIndex !== index) {
                    return holding;
                }

                const updatedHolding = {...holding};
                changeFunction(updatedHolding);
                return updatedHolding;
            }));
        }
    }

    function navigateToEditLegalAgreement() {
        history.push(`/Profile/${profile.id}/ClientProfile/${viewType}/EditLegalAgreement/${legalAgreementId}`);
    }

    const handleDone = async () => {
        page(`Legal_Agreement_Holding_Done_Click`);
        const changedHoldings = updatedDomesticTrustHoldings
            .filter((trustHoldings, index) => {
                return trustHoldings.locked !== domesticTrustHoldings[index].locked
                    || trustHoldings.concentrated !== domesticTrustHoldings[index].concentrated
            });
        if (changedHoldings.length === 0) {
            navigateToEditLegalAgreement();
            return;
        }

        setIsDoneButtonDisabled(true);
        setShowNavigationModal(false);

        try {
            if (enableOneWDSMigration) {
                const requests = changedHoldings.map(trustHolding => {
                    if (!trustHolding.accountNumber) {
                        throw new Error("Missing account number for legal agreement position");
                    }
                    const isSma = trustHolding.managementType === "SMA";
                    return {
                        accountNumber: trustHolding.accountNumber,
                        ...(isSma ? {} : {
                            securityId: trustHolding.securityId,
                            cusip: trustHolding.cusip
                        }),
                        managementType: trustHolding.managementType,
                        locked: trustHolding.locked,
                        concentrated: trustHolding.concentrated
                    };
                });
                await assetsApiClient.postLegalAgreementPosition(profile.id, requests);
            } else {
                //TODO enableOneWDSMigration cleanup: Remove this Save logic once this feature is in Prod
                const updatedHoldings: DomesticTrustHoldingWriteModel[] = changedHoldings.map(trustHolding => {
                const domesticTrustHoldingWriteModel: DomesticTrustHoldingWriteModel = {
                    holdingPersistentId: trustHolding.id!,
                    locked: trustHolding.locked,
                    concentrated: trustHolding.concentrated
                }
                return domesticTrustHoldingWriteModel;
            });
                const response = await assetsApiClient.putLegalAgreementHoldings(
                    profile.id,
                    {domesticTrustHoldings: updatedHoldings}
                );
                if (response.status !== 200) {
                    throw new Error();
                }
            }

            dispatch(updateInvestmentProgramLegalAgreementHoldings({
                legalAgreementId,
                allHoldings: updatedDomesticTrustHoldings
            }));
            navigateToEditLegalAgreement();
        } catch {
            setIsDoneButtonDisabled(false);
            setShowNavigationModal(true);
        }
    }

    if (!domesticTrustHoldings || !legalAgreement || !classifications) {
        return <LoadingIndicator/>
    }

    const assetSubclassDetailsRowRenderer = (assetSubclassDetail: HoldingAssetSubclassDetails, index: number) =>
        <IPAccountsHoldingSummarySubclassDetailsRow
            key={`asset-subclass-details-${assetSubclassDetail.id}-${assetSubclassDetail.productName}`}
            index={index}
            assetSubclassDetail={assetSubclassDetail}
            handleConcentrateHolding={handleConcentrateHolding}
            handleLockHolding={handleLockHolding}
            disableCheckboxes={isProfileWithProposalsOrArchived!}
        />;

    return (
        <div className="legal-agreement-holdings asset-form holdings-container">
            <div className="layout-data-entry-form">
                <DataEntryHeader
                    className='dataEntryHeader'
                    title={legalAgreement.name}
                    onPrimaryButtonClick={handleDone}
                    disablePrimaryButton={isDoneButtonDisabled}
                    primaryButtonText="Done"
                    hideSecondaryButton={true}
                />
            </div>
            <HoldingsInfo
                account={{
                    institution: legalAgreement.institution,
                    accountNumber: legalAgreement.legalAgreementNumber,
                    taxStatus: legalAgreement.taxStatus,
                    memberOwnerships: [],
                    legalEntityOwnerships: [],
                    ownershipCategory: "Sole"
                }}
                allHoldings={updatedDomesticTrustHoldings}
                classifications={classifications}
            />
            <div className="holding-grid holding-grid-container">
                <div className="grid-display-contents" role="row">
                        <span className="display-flex align-items-center paddingbottom-md"
                              role="cell">
                            <Icon name="chevron_double_right"/>
                            <span className="condensed-subtitle paddingleft-md">Asset Class / Subclass</span>
                        </span>
                    <span className="condensed-subtitle" role="cell">Product</span>
                    <span className="textalign-right condensed-subtitle" role="cell">Account</span>
                    <span className="condensed-subtitle textalign-right" role="cell">Tax Cost</span>
                    <span className="condensed-subtitle textalign-right" role="cell">Market Value</span>
                    <span className="condensed-subtitle textalign-right" role="cell">Investable Value</span>
                    <span className="condensed-subtitle textalign-center" role="cell">Locked</span>
                    <span className="condensed-subtitle textalign-left" role="cell">Concen</span>
                </div>
                <div className="empty-grid-row"/>
                <HoldingsScrollableContainer>
                    <HoldingSummaryAccordion
                        riskAssetClasses={classifications?.riskAssetClasses}
                        riskControlAssetClasses={classifications?.riskControlAssetClasses}
                        assetSubclassRowRenderer={(assetSubclass, holdingsWithIndex, subclassIndex) => {
                            return <HoldingSummaryAssetSubclass
                                key={subclassIndex}
                                assetSubclass={assetSubclass}
                                holdingsWithIndex={holdingsWithIndex}
                                subclassIndex={subclassIndex}
                                assetSubclassDetailsRowRenderer={assetSubclassDetailsRowRenderer}/>
                        }}
                        selectedHoldings={domesticTrustHoldings}
                    />
                </HoldingsScrollableContainer>
            </div>
            <HistoryBlockDiscardModal
                when={formChanged && showNavigationModal}
            />
        </div>
    )
}
